// Collapse free-text supplier-name variants ("Acme electronics Supplier",
// "Acme Electronics Suppliers", "Acme ELectronics suppliers", …) into one canonical option so
// the confirmed-order supplier dropdown shows a single entry per real supplier. (QA Ticket C)

function normKey(name: string): string {
  return String(name || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

function levenshtein(a: string, b: string): number {
  const m = a.length, n = b.length;
  if (!m) return n;
  if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i += 1) {
    const cur = [i];
    for (let j = 1; j <= n; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
    }
    prev = cur;
  }
  return prev[n];
}

function similar(a: string, b: string): number {
  const x = normKey(a), y = normKey(b);
  if (!x || !y) return 0;
  const maxLen = Math.max(x.length, y.length);
  return maxLen === 0 ? 1 : 1 - levenshtein(x, y) / maxLen;
}

// 0.82 catches case + singular/plural + one/two-letter typos of a real supplier name without
// merging genuinely different suppliers. ponytail: fixed threshold; tune if false-merges appear.
const MERGE_THRESHOLD = 0.82;

// Prefer the "cleanest-looking" variant as the label: most capitalised words, then longest.
function betterLabel(a: string, b: string): string {
  const caps = (s: string) => (s.match(/[A-Z]/g) || []).length;
  if (caps(b) !== caps(a)) return caps(b) > caps(a) ? b : a;
  return b.length > a.length ? b : a;
}

export interface SupplierCanonicalizer {
  canonicalOf: (name: string) => string;
  canonicalList: (names: string[]) => string[];
}

/**
 * Build a canonicaliser from the full set of supplier names seen on an order. Names within the
 * similarity threshold collapse to a single canonical label.
 */
export function buildSupplierCanonicalizer(allNames: string[]): SupplierCanonicalizer {
  const groups: { label: string; members: Set<string> }[] = [];

  for (const raw of allNames) {
    const name = String(raw || '').trim();
    if (!name) continue;
    const g = groups.find((grp) => Array.from(grp.members).some((mm) => similar(mm, name) >= MERGE_THRESHOLD));
    if (g) {
      g.members.add(name);
      g.label = betterLabel(g.label, name);
    } else {
      groups.push({ label: name, members: new Set([name]) });
    }
  }

  const keyToLabel = new Map<string, string>();
  groups.forEach((g) => Array.from(g.members).forEach((m) => keyToLabel.set(normKey(m), g.label)));

  const canonicalOf = (name: string): string => {
    const n = String(name || '').trim();
    return keyToLabel.get(normKey(n)) || n;
  };

  const canonicalList = (names: string[]): string[] => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const n of names) {
      const c = canonicalOf(n);
      if (c && !seen.has(c)) { seen.add(c); out.push(c); }
    }
    return out;
  };

  return { canonicalOf, canonicalList };
}
